// Centralised resilience + observability for Supabase Realtime subscriptions.
//
// A Supabase channel reports its lifecycle through `channel.subscribe((status, err) => …)`,
// emitting one of: 'SUBSCRIBED' | 'CHANNEL_ERROR' | 'TIMED_OUT' | 'CLOSED'.
//
// Before TAS-5 these non-SUBSCRIBED statuses were swallowed (no-op callbacks or
// fire-and-forget `.subscribe()`), so a dropped channel was completely invisible
// to monitoring — and in the lobby it could strand every player on "ready" with
// the game never starting. This module makes every failure loud: it logs to both
// Sentry (for engineering/alerting) and Mixpanel (so drops sit alongside the
// product/game funnel and can be correlated with stuck sessions).

import { track } from '@tasted/utils/analytics';
import { Sentry, logger } from './sentry';

// Statuses that indicate a genuine realtime FAILURE worth alerting on.
// NB: 'CLOSED' is intentionally excluded. Supabase emits CLOSED on every normal
// teardown — `channel.unsubscribe()` / `supabase.removeChannel()` fired from a
// useEffect cleanup as a screen unmounts or the game advances a step. Treating it
// as a failure floods Sentry + Mixpanel with routine lifecycle events (a single
// session progressing through the game produced 100s of "Realtime CLOSED" issues).
// A genuinely dropped connection surfaces as CHANNEL_ERROR or TIMED_OUT first, so
// real drops are still caught.
const FAILURE_STATUSES = new Set(['CHANNEL_ERROR', 'TIMED_OUT']);

// Feature flag (default ON — TAS-176 re-do). Gates the realtime-resilience
// layer — the failure logging here plus the lobby's auto-reconnect + polling
// fallback. Codex-verified safe to enable independently: it only gates
// reconnect/polling/reporting, never the REST join path. EMERGENCY off-switch:
//   localStorage.setItem('ff_realtime_resilience', 'false')
//   or VITE_REALTIME_RESILIENCE=false in .env / Vercel env
export function isRealtimeResilienceEnabled() {
  try {
    const override = localStorage.getItem('ff_realtime_resilience');
    if (override === 'true') return true;
    if (override === 'false') return false;
  } catch {
    /* localStorage unavailable — fall through to env/default */
  }
  return import.meta.env?.VITE_REALTIME_RESILIENCE !== 'false';
}

// Migration 184 removes direct answer writes. Ignore obsolete rollout overrides
// so a device with an old flag still submits through the protected scoring API.
export function isServerScoringEnabled() {
  return true;
}

// Feature flag (default ON — TAS-178). Gates the session_live_state reconcile:
// the GM's dual-write to the authoritative room row + a stuck client reading it
// to catch up on reconnect/focus/mount/flowSteps-load. Additive + forward-only —
// the broadcast + gm-next sync is unchanged; this only ever moves a STUCK client
// forward. Validated dark, now on by default. EMERGENCY off-switch:
//   localStorage.setItem('ff_live_state_reconcile', 'false')
//   or VITE_LIVE_STATE_RECONCILE=false in .env / Vercel env
export function isLiveStateReconcileEnabled() {
  try {
    const override = localStorage.getItem('ff_live_state_reconcile');
    if (override === 'true') return true;
    if (override === 'false') return false;
  } catch {
    /* localStorage unavailable — fall through to env/default */
  }
  return import.meta.env?.VITE_LIVE_STATE_RECONCILE !== 'false';
}

// TAS-271 PR-4 (default ON). Gates payload-driven realtime updates: answer
// counters, powerplay summaries, and avatar-taken sets apply the event's own
// row/payload instead of re-querying the table on every event — the re-query
// pattern was O(N²) DB reads per round and the main 16-player scaling risk.
// Reconcile paths (seed on subscribe/reconnect/focus) are unchanged.
// EMERGENCY off-switch (reverts to full re-fetch per event):
//   localStorage.setItem('ff_payload_counts', 'false')
//   or VITE_PAYLOAD_COUNTS=false in .env / Vercel env
export function isPayloadCountsEnabled() {
  try {
    const override = localStorage.getItem('ff_payload_counts');
    if (override === 'true') return true;
    if (override === 'false') return false;
  } catch {
    /* localStorage unavailable — fall through to env/default */
  }
  return import.meta.env?.VITE_PAYLOAD_COUNTS !== 'false';
}

// TAS-271 PR-5 (default ON). Gates the sync-request storm cuts: requests fire
// only on REconnect (not first subscribe — first-mount catch-up is already
// covered by the resume effect, the gm-next re-check on flowSteps load, the
// live-state read, and useGameEvents' SUBSCRIBED query), and both the request
// and the wake-up reconcile are jittered so N co-located phones don't burst
// the GM/backend in the same instant (~2N² deliveries at 16 players).
// EMERGENCY off-switch:
//   localStorage.setItem('ff_sync_gating', 'false')
//   or VITE_SYNC_GATING=false in .env / Vercel env
export function isSyncGatingEnabled() {
  try {
    const override = localStorage.getItem('ff_sync_gating');
    if (override === 'true') return true;
    if (override === 'false') return false;
  } catch {
    /* localStorage unavailable — fall through to env/default */
  }
  return import.meta.env?.VITE_SYNC_GATING !== 'false';
}

// TAS-271 PR-5: should this client broadcast a sync-request on SUBSCRIBED?
// GM never asks (it answers); followers ask only on a REsubscribe, where a
// gap may actually exist.
export function shouldSendSyncRequest({ isGm = false, hadSubscribed = false } = {}) {
  if (isGm) return false;
  return hadSubscribed;
}

// TAS-271 PR-5: uniform jitter in [0, maxMs). `rand` injectable for tests.
export function jitterDelay(maxMs, rand = Math.random) {
  const max = Number.isFinite(maxMs) && maxMs > 0 ? maxMs : 0;
  return Math.round(rand() * max);
}

// TAS-20 phase 2b: verify step-advance broadcasts against the authoritative
// session_live_state and roll back a forged/unconfirmed optimistic advance.
// DEFAULT OFF — this changes the core sync path (it can rewind a step), so it must
// be tuned on staging with real devices before enabling. Requires the live-state
// reconcile (relies on session_live_state). Toggle:
//   localStorage.setItem('ff_broadcast_verify', 'true')  or  VITE_BROADCAST_VERIFY=true
export function isBroadcastVerifyEnabled() {
  try {
    const override = localStorage.getItem('ff_broadcast_verify');
    if (override === 'true') return true;
    if (override === 'false') return false;
  } catch {
    /* localStorage unavailable — fall through to env/default */
  }
  return import.meta.env?.VITE_BROADCAST_VERIFY === 'true';
}

// TAS-274 (default OFF). Gates "TV mode": the Game View Mode selection step, the
// standalone /tv big-screen display, and the phone "look at the big screen"
// behaviour. DEFAULT OFF so the whole feature ships inert — when off,
// GameFlowProvider forces gameViewMode to 'phone' and every guarded branch is a
// no-op, leaving Phone mode byte-for-byte unchanged.
//
// Three ways in, in this order:
//   1. localStorage.setItem('ff_tv_mode', 'true' | 'false') — a device override
//      that wins either way, for testing a session the admin did not flag.
//   2. VITE_TV_MODE=true — the whole build.
//   3. `features.tv_mode` on the session — the admin's per-game switch, set in
//      the create-session wizard alongside Tutor and the prize page.
// Called with no argument (before session features are known) it answers on the
// first two alone, which is the old behaviour exactly.
export function isTvModeEnabled(features) {
  try {
    const override = localStorage.getItem('ff_tv_mode');
    if (override === 'true') return true;
    if (override === 'false') return false;
  } catch {
    /* localStorage unavailable — fall through to env/session/default */
  }
  if (import.meta.env?.VITE_TV_MODE === 'true') return true;
  return features?.tv_mode === true;
}

// TAS-151: one WebSocket drop fans out as a CHANNEL_ERROR on EVERY active
// channel at once (RealtimeClient._onConnClose → _triggerChanError →
// channels.forEach), so a single mobile connection blip reported N exceptions
// × N users — dozens of Sentry "issues" that were really one event. Collapse
// the fan-out: once a failure is reported, further failures within
// FANOUT_WINDOW_MS ride along silently (they're the same connection event),
// and the same (channel, status) pair reports at most once per
// REPEAT_WINDOW_MS so a flapping connection doesn't re-report every retry.
// One reported event per real drop also makes the Mixpanel drop count honest —
// it counts connection drops, not open channels × drops. Handlers are
// unaffected: loggedSubscribe forwards every status regardless.
const FANOUT_WINDOW_MS = 2_500;
const REPEAT_WINDOW_MS = 30_000;
let lastReportAt = 0;
const lastReportByChannelStatus = new Map();

// TAS-271: distinct reported drops this page lifetime. Lets the dashboard
// separate "one bad moment" from "flapping all game" without joining events.
let sessionDropCount = 0;

// TAS-271: device-side context on every failure report, so device-sleep
// wake-ups, venue wifi, and Supabase-side incidents are separable in Mixpanel.
function connectionMeta() {
  try {
    const conn = typeof navigator !== 'undefined' ? navigator.connection : null;
    return {
      connection_type: conn?.effectiveType || null,
      visibility:
        typeof document !== 'undefined' ? document.visibilityState : null,
    };
  } catch {
    return {};
  }
}

// Test-only: clear the throttle state between test cases.
export function __resetReportThrottle() {
  lastReportAt = 0;
  lastReportByChannelStatus.clear();
  sessionDropCount = 0;
}

/**
 * Report a realtime subscription status to Sentry + Mixpanel when it indicates
 * a failure. Safe to call for every status — 'SUBSCRIBED' and unknown statuses
 * are ignored. Never throws: observability must not break gameplay.
 *
 * @param {string} status  The status from channel.subscribe()
 * @param {object} meta    Context, e.g. { channel, sessionCode, context }
 * @param {*}      [err]   Optional error passed by Supabase alongside the status
 */
export function reportSubscriptionStatus(status, meta = {}, err) {
  if (!FAILURE_STATUSES.has(status)) return;
  if (!isRealtimeResilienceEnabled()) return;

  const context = { status, ...meta };

  const now = Date.now();
  // Prune expired throttle entries so the map stays bounded on a long-lived SPA.
  for (const [k, t] of lastReportByChannelStatus) {
    if (now - t >= REPEAT_WINDOW_MS) lastReportByChannelStatus.delete(k);
  }
  const key = `${meta.channel || 'unknown'}:${status}`;
  const lastForKey = lastReportByChannelStatus.get(key) || 0;
  if (now - lastReportAt < FANOUT_WINDOW_MS || now - lastForKey < REPEAT_WINDOW_MS) {
    // Same connection event (fan-out) or a recent repeat. A breadcrumb only —
    // it rides along on future Sentry events without sending anything itself
    // (logger here is Sentry.logger, so a warn would just be N logs instead
    // of N exceptions).
    try {
      Sentry.addBreadcrumb?.({
        category: 'realtime',
        level: 'warning',
        message: 'realtime subscription failure (suppressed duplicate)',
        data: context,
      });
    } catch {
      /* swallow — never let observability break gameplay */
    }
    return;
  }
  lastReportAt = now;
  lastReportByChannelStatus.set(key, now);
  // Dashboards should read this as "a connection-level failure occurred, with
  // meta from the first channel that reported" — not a per-channel count.
  context.deduped = true;
  sessionDropCount += 1;
  context.session_drop_count = sessionDropCount;
  Object.assign(context, connectionMeta());

  // Sentry — a structured log plus an exception so it groups into an issue.
  try {
    logger?.error?.('realtime subscription failure', context);
    Sentry.captureException(
      err instanceof Error
        ? err
        : new Error(`Realtime ${status}: ${meta.channel || 'unknown channel'}`),
      {
        tags: {
          realtime_status: status,
          realtime_channel: meta.channel,
        },
        extra: context,
      }
    );
  } catch {
    /* swallow — never let observability break gameplay */
  }

  // Mixpanel — so subscription drops are visible in product analytics and can be
  // correlated with sessions that fail to progress.
  try {
    track('Realtime Subscription Failed', context);
  } catch {
    /* swallow analytics errors */
  }
}

/**
 * Build a `channel.subscribe()` status callback that auto-reports failures and
 * then forwards to an optional caller-provided handler. Lets a fire-and-forget
 * `.subscribe()` become `.subscribe(loggedSubscribe({ channel, sessionCode }))`
 * with no other changes.
 *
 * @param {object}   meta        Context, e.g. { channel, sessionCode, context }
 * @param {Function} [onStatus]  Optional handler called with (status, err)
 * @returns {Function} a (status, err) => void callback for channel.subscribe()
 */
export function loggedSubscribe(meta = {}, onStatus) {
  return (status, err) => {
    reportSubscriptionStatus(status, meta, err);
    if (typeof onStatus === 'function') onStatus(status, err);
  };
}

// ── Reconnecting channels (TAS-174) ─────────────────────────────────────────
//
// A subscribed channel can never be re-subscribed, so page-level channels that
// hit one CHANNEL_ERROR/TIMED_OUT historically died for the rest of the screen.
// resilientChannel() owns the create → bind → subscribe → reconnect-with-backoff
// lifecycle for every page channel, GameFlowProvider's and useAnswerSync's included.
//
// Topic rules:
//  - postgres_changes channels: pass `topic` as a FUNCTION returning a unique
//    name per attempt (supabase.channel(topic) returns any still-registered
//    instance for a repeated topic, and .on('postgres_changes') on it throws —
//    TAS-150/171).
//  - broadcast channels: the topic is shared across clients, so pass a STRING;
//    the helper AWAITS removal of the stale instance before recreating.
import { supabase } from '@tasted/utils/supabase';

// TAS-179: reconnect backoff with 50–100% jitter. Base is exponential (1s, 2s,
// 4s … capped at 30s); the actual delay is a random point in [50%, 100%] of
// base, so N clients that dropped together (one flaky venue AP) spread their
// resubscribes across a 2:1 window instead of retrying in synchronized waves.
// Exported + `rand` injectable purely for deterministic tests.
export function nextReconnectDelay(attempts, rand = Math.random) {
  const base = Math.min(1000 * 2 ** attempts, 30000);
  return Math.round(base * (0.5 + rand() * 0.5));
}

// Review of C4: realtime-js prefixes every topic with "realtime:".
const REALTIME_TOPIC_PREFIX = 'realtime:';
const LIVE_CHANNEL_STATES = new Set(['joined', 'joining']);

/** Await removal of registered, non-live channels for `name` (see connect()). */
async function removeStaleTopicChannels(name) {
  let registered = [];
  try {
    registered = supabase.getChannels?.() || [];
  } catch {
    return;
  }
  const stale = registered.filter(
    (ch) => ch?.topic === `${REALTIME_TOPIC_PREFIX}${name}` && !LIVE_CHANNEL_STATES.has(ch?.state)
  );
  for (const ch of stale) {
    try {
      await supabase.removeChannel(ch);
    } catch {
      /* already gone */
    }
  }
}

// C3: a channel that connected this recently is a game still being played
// (a long game night runs about three hours), and while the phone is visible
// an exhausted channel of that kind tries again this often.
export const LIVE_WINDOW_MS = 3 * 60 * 60 * 1000;
export const LIVE_REVIVE_MS = 60 * 1000;

/**
 * @param {object}   opts
 * @param {string|Function} opts.topic  channel topic, or () => topic for unique-per-attempt names
 * @param {Function} opts.setup         (channel) => void — attach .on(...) bindings
 * @param {object}   [opts.meta]        reportSubscriptionStatus meta ({ sessionCode, context })
 * @param {object}   [opts.config]      supabase channel config (e.g. { config: { broadcast: { self: true } } })
 * @param {Function} [opts.onChannel]   (channel|null) => void — track the live instance (for send() refs)
 * @param {Function} [opts.onStatus]    (status, err) => void passthrough
 * @param {number}   [opts.maxAttempts] reconnect attempts before giving up (default 5)
 * @param {number}   [opts.maxRevivals] wake-up revivals allowed after exhausting
 *                                      maxAttempts, before the channel stays
 *                                      dead for good (default 3). A channel
 *                                      that was connected within liveWindowMs
 *                                      is not held to this budget (C3).
 * @param {number}   [opts.liveWindowMs] how recently the channel must have been
 *                                      SUBSCRIBED to count as a live game
 *                                      (default 3 h)
 * @param {number}   [opts.liveReviveMs] while live, visible and exhausted, try
 *                                      again this often (default 60 s)
 * @param {number}   [opts.reviveJitterMs] wake-up / online revives wait a
 *                                      random 0..this (default 2 s)
 * @returns {Function} dispose() — tear the channel down (call from effect cleanup)
 */
export function resilientChannel({
  topic,
  setup,
  meta = {},
  config,
  onChannel,
  onStatus,
  maxAttempts = 5,
  maxRevivals = 3,
  liveWindowMs = LIVE_WINDOW_MS,
  liveReviveMs = LIVE_REVIVE_MS,
  reviveJitterMs = 2000,
}) {
  let disposed = false;
  let reconnectTimer = null;
  let liveReviveTimer = null;
  let attempts = 0;
  let gaveUp = false;
  let current = null;
  // TAS-317: how many times the visibility handler has resurrected an
  // exhausted channel. Reset on a successful SUBSCRIBED — a real game night
  // with a flaky venue should keep its revivals — but never unbounded.
  let revivals = 0;
  // C3 (edge-case audit 2026-09-28): when this channel last reached
  // SUBSCRIBED — or, until it first does, when it was created (a page loaded
  // while offline is a game being played too). A channel live within
  // liveWindowMs belongs to a game that is still being played, so it keeps
  // reviving past maxRevivals while the phone is in the player's hand. A tab
  // left on a game that ended weeks ago (TAS-317's zombie) was last live long
  // before that, so the budget still stops it.
  const createdAt = Date.now();
  let lastSubscribedAt = 0;
  const recentlyLive = () => Date.now() - (lastSubscribedAt || createdAt) < liveWindowMs;
  const isVisible = () => typeof document === 'undefined' || document.visibilityState === 'visible';

  const connect = async () => {
    if (disposed) return;

    if (current) {
      const stale = current;
      current = null;
      onChannel?.(null);
      try {
        await supabase.removeChannel(stale);
      } catch {
        /* channel already dead */
      }
      if (disposed) return;
    }

    const name = typeof topic === 'function' ? topic() : topic;
    // Review of C4: removeChannel() resolves after the server acknowledges the
    // leave, and until then supabase.channel(topic) hands back the LEAVING
    // instance, on which subscribe() is a silent no-op — the shared-topic
    // game-sync channel stayed dead after an effect re-run (isGm flipping).
    // Wait out every still-registered, non-live instance of this topic first.
    // A joined or joining one belongs to a live subscriber and is left alone.
    await removeStaleTopicChannels(name);
    if (disposed) return;
    let channel;
    try {
      channel = config ? supabase.channel(name, config) : supabase.channel(name);
      setup(channel);
    } catch (err) {
      scheduleRetryAfterThrow(name, err);
      return;
    }
    // `current` must be set BEFORE subscribe(): a synchronously-delivered
    // status would otherwise fail the `channel !== current` guard below and
    // vanish — no report, no retry (codex review).
    current = channel;
    onChannel?.(channel);
    const handleStatus = (status, err) => {
      if (disposed || channel !== current) return;
      if (status === 'SUBSCRIBED') {
        attempts = 0;
        gaveUp = false;
        revivals = 0; // TAS-317: a working connection earns its revivals back.
        lastSubscribedAt = Date.now();
        if (liveReviveTimer) { clearTimeout(liveReviveTimer); liveReviveTimer = null; }
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        // TAS-271: quiet first retry. A phone waking from sleep (long wine
        // discussions!) drops every channel at once and the first resubscribe
        // almost always succeeds — reporting that as a failure produced most
        // of game night's 77 events. Report only failures that SURVIVE one
        // automatic retry; the first one leaves a breadcrumb and retries
        // silently — but ONLY when a retry is actually coming (attempts <
        // maxAttempts); a failure that exhausts the budget always reports.
        if (attempts >= 1 || attempts >= maxAttempts) {
          reportSubscriptionStatus(status, { channel: name, attempt: attempts, ...meta }, err);
        } else {
          try {
            Sentry.addBreadcrumb?.({
              category: 'realtime',
              level: 'warning',
              message: 'realtime subscription failure (quiet first retry)',
              data: { status, channel: name, ...meta },
            });
          } catch {
            /* swallow — never let observability break gameplay */
          }
        }
        if (attempts < maxAttempts) {
          // TAS-179: jittered exponential backoff (see nextReconnectDelay) so
          // a shared drop — a whole co-located party on one flaky access
          // point — doesn't resubscribe in lockstep and hammer the backend in
          // synchronized waves.
          const delay = nextReconnectDelay(attempts);
          if (reconnectTimer) clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(() => {
            if (disposed) return;
            attempts += 1;
            // TAS-180: reconnect attempts are the "how flaky is a real venue"
            // data point. The FIRST retry stays quiet (TAS-271 — one blip is
            // normal life); from the second on, count them.
            if (attempts >= 2) {
              try { track('Reconnect Attempt', { channel: name, attempt: attempts, delayMs: delay, ...(meta || {}) }); } catch { /* */ }
            }
            connect();
          }, delay);
        } else if (!gaveUp) {
          // TAS-179: no silent give-up. Once reconnects are exhausted, surface
          // it to telemetry once (Sentry + Mixpanel) so the resilience
          // dashboard and the recovery UX can react instead of the socket
          // quietly dying after maxAttempts.
          gaveUp = true;
          logger?.error?.('realtime reconnect exhausted', { channel: name, attempts, ...meta });
          track('Realtime Reconnect Exhausted', { channel: name, attempts, ...(meta || {}) });
          scheduleLiveRevive();
        }
      }
      if (typeof onStatus === 'function') onStatus(status, err);
    };
    try {
      channel.subscribe(handleStatus);
    } catch (err) {
      // "tried to subscribe multiple times": treat it as a failed join, so
      // the normal backoff rebuilds the channel instead of it dying here.
      handleStatus('CHANNEL_ERROR', err);
    }
  };

  // setup() or channel() threw before there was a channel to report on.
  function scheduleRetryAfterThrow(name, err) {
    current = null;
    onChannel?.(null);
    reportSubscriptionStatus('CHANNEL_ERROR', { channel: name, attempt: attempts, ...meta }, err);
    if (attempts >= maxAttempts) {
      gaveUp = true;
      scheduleLiveRevive();
      return;
    }
    const delay = nextReconnectDelay(attempts);
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(() => {
      if (disposed) return;
      attempts += 1;
      connect();
    }, delay);
  }

  // TAS-271: wake-up revival. Backgrounded tabs throttle timers, so a phone
  // that slept through all maxAttempts backoffs used to wake with a silently
  // dead channel for the rest of the screen. On return to visibility, give an
  // exhausted channel one fresh, immediate reconnect cycle (counters reset —
  // the network context has genuinely changed).
  //
  // TAS-317: bounded. Revival used to be unconditional, so a tab left open on
  // a FINISHED game restarted the whole backoff cycle every time the device
  // was unlocked — forever. Test session 4585 ended 2026-07-31 and was still
  // producing failures on 2026-08-21: 165 Realtime Subscription Failed + 50
  // Connection Lost between 18–21 Aug, ~65% of all realtime failures in that
  // window, from a game that had been over for three weeks. It burned realtime
  // quota and drowned out every genuine drop in Sentry and Mixpanel.
  //
  // A live game re-earns its budget on every successful SUBSCRIBED, so a real
  // venue with a flaky access point is unaffected; only a channel that can
  // never connect again runs out and stays down.
  //
  // Codex review raised the converse: a LIVE game that fails maxAttempts and
  // then every revival can no longer reconnect on its own, even once the
  // network comes back. That takes 8 failed cycles in a row, at which point
  // the device has no working connection to Supabase at all — and the player
  // still has the banner's recoverSession() CTA, which re-authenticates and
  // reloads, rebuilding every channel with a fresh budget.
  //
  // C3 (edge-case audit 2026-09-28) closed that gap without a per-call-site
  // predicate: "live" is read off the channel itself. One that reached
  // SUBSCRIBED within liveWindowMs is a game in progress, and it is refunded:
  // it revives past maxRevivals, on wake-up, on `online`, and every
  // liveReviveMs while the tab stays visible (a player staring at a frozen
  // question never fires visibilitychange). A tab whose last connection is
  // older than the window is held to the budget exactly as before.
  const canRevive = () => revivals < maxRevivals || recentlyLive();

  const revive = () => {
    if (disposed || !gaveUp || !isVisible() || !canRevive()) return;
    if (liveReviveTimer) { clearTimeout(liveReviveTimer); liveReviveTimer = null; }
    revivals += 1;
    gaveUp = false;
    attempts = 0;
    connect();
  };

  function scheduleLiveRevive() {
    if (disposed || !recentlyLive() || !(liveReviveMs > 0)) return;
    if (liveReviveTimer) clearTimeout(liveReviveTimer);
    liveReviveTimer = setTimeout(() => {
      liveReviveTimer = null;
      if (disposed || !gaveUp) return;
      // Hidden: the visibility handler revives on return; keep checking so a
      // phone that stays visible but was briefly covered still recovers.
      if (!isVisible()) { scheduleLiveRevive(); return; }
      revive();
    }, liveReviveMs);
  }

  // Jittered (0–2 s) like the other wake-up paths: a whole party unlocking
  // their phones together must not resubscribe in the same instant.
  let wakeTimer = null;
  const reviveSoon = () => {
    if (disposed || !gaveUp || wakeTimer) return;
    wakeTimer = setTimeout(() => { wakeTimer = null; revive(); }, jitterDelay(reviveJitterMs));
  };
  const onVisible = () => reviveSoon();
  const onOnline = () => reviveSoon();
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisible);
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('online', onOnline);
  }

  connect();

  return () => {
    disposed = true;
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisible);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', onOnline);
    }
    if (liveReviveTimer) clearTimeout(liveReviveTimer);
    if (wakeTimer) clearTimeout(wakeTimer);
    if (reconnectTimer) clearTimeout(reconnectTimer);
    if (current) {
      supabase.removeChannel(current);
      current = null;
    }
    onChannel?.(null);
  };
}
